import { DateTime as DT } from 'luxon';
import type { TFunction } from 'i18next';
import type { ProposalExtraItemType, ProposalProductItemType } from '@econtractor/types';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { hasHtmlContent } from 'utils/helpers/functions/stripHtml';
import {
    getProposalDisplayPrice,
    getProposalLineTotal,
    getProposalLineVat,
    getProposalTotalAmount,
    getProposalTotalVatAmount,
    getProposalUseUsd,
} from './proposalPriceUtils';
import type { TemplateValueKeysHelpers } from '../useTemplateValueKeys';
import { buildObjectTemplateValueKeysMap } from '../buildObjectTemplateValueKeys';
import { buildDocumentTemplateValueKeysMap } from '../buildDocumentTemplateValueKeys';
import {
    hasTemplateTableMarker,
    normalizeTemplateMarkers,
    TEMPLATE_MARKER_REGEX,
} from './templateMarkers';
import { loadObjectWarranties } from './loadObjectWarranties';
import { fillMissingLineDimensions } from './fillLineDimensions';
import type { ProposalEditFormValues } from '../../../Proposal/types';
import {
    getBaseCurrencyCode,
    getCurrencyCode,
    getCurrencyI18nParams,
} from 'utils/helpers/currency/currencyHelpers';

interface ProposalPdfRow {
    name: string;
    unit: string;
    count: number;
    price: number;
    total: number;
    vat: number;
}

interface BuildProposalPdfDefinitionParams {
    t: (key: string, options?: Record<string, unknown>) => string;
    tObjects?: (key: string, options?: Record<string, unknown>) => string;
    templateValueKeys: TemplateValueKeysHelpers;
    formData: ProposalEditFormValues;
    rate: number;
    generatedAt: number;
    objectData?: unknown;
    logoDataUrl?: string;
    backgroundDataUrl?: string;
    resolveImageDataUrl?: (src: string) => Promise<string>;
    /** Cover/title page — only for commercial proposals. Documents skip it. */
    includeCoverPage?: boolean;
    /**
     * Commercial proposals append the specification table when the template
     * has no table marker. Documents do not.
     */
    appendSpecificationWithoutMarker?: boolean;
}

const formatDimension = (
    t: (key: string, options?: Record<string, unknown>) => string,
    i18nKey?: string,
    name?: string,
): string => tDirName(t as TFunction, i18nKey, name);

const mapProductRow = (
    row: ProposalProductItemType,
    rate: number,
    useUsd: boolean,
    withVat: boolean,
    t: (key: string, options?: Record<string, unknown>) => string,
): ProposalPdfRow => {
    const price = getProposalDisplayPrice(row, rate, useUsd);
    const total = getProposalLineTotal(row, rate, useUsd);
    return {
        name: row.prodName || row.sourceName || `#${row.productId}`,
        unit: formatDimension(t, row.dimensionI18nKey, row.dimensionName),
        count: Number(row.itemCount || 0),
        price,
        total,
        vat: getProposalLineVat(row, rate, useUsd, withVat),
    };
};

const mapAdditionalRow = (
    row: ProposalExtraItemType,
    rate: number,
    useUsd: boolean,
    withVat: boolean,
    t: (key: string, options?: Record<string, unknown>) => string,
): ProposalPdfRow => {
    const price = getProposalDisplayPrice(row, rate, useUsd);
    const total = getProposalLineTotal(row, rate, useUsd);
    return {
        name: row.itemName || '-',
        unit: formatDimension(t, row.dimensionI18nKey, row.dimensionName),
        count: Number(row.itemCount || 0),
        price,
        total,
        vat: getProposalLineVat(row, rate, useUsd, withVat),
    };
};

const MAX_LINE_LENGTH = 500;

const normalizeLine = (line: string): string =>
    String(line || '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, MAX_LINE_LENGTH);

const formatWarrantyDate = (raw: string): string => {
    const date = DT.fromISO(String(raw || '').slice(0, 10));
    return date.isValid ? date.toFormat('dd.MM.yyyy') : '';
};

/** Collapse whitespace inside one inline run and keep a single edge space.
 *  Trimming here deletes the gap in `text <strong>bold</strong> text`.
 *  Do not cap length: a document paragraph is one run and must reach the PDF whole. */
const normalizeInlineText = (line: string): string => {
    const raw = String(line || '');
    if (!raw) return '';
    return raw.replace(/\s+/g, ' ');
};

type PdfTextDecoration = 'underline' | 'lineThrough';

type PdfTextMarks = {
    bold?: boolean;
    italics?: boolean;
    decoration?: PdfTextDecoration;
    color?: string;
    background?: string;
};

type PdfTextAlignment = 'left' | 'center' | 'right' | 'justify';

/** Convert CSS color (hex/rgb/rgba/named) to a pdfmake-friendly hex when possible. */
const cssColorToPdf = (value: string): string | undefined => {
    const raw = String(value || '').trim();
    if (!raw) return undefined;
    if (raw === 'transparent' || raw === 'inherit' || raw === 'initial' || raw === 'currentcolor') {
        return undefined;
    }
    if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw)) {
        if (raw.length === 4) {
            const r = raw[1];
            const g = raw[2];
            const b = raw[3];
            return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
        }
        return raw.slice(0, 7).toUpperCase();
    }
    const rgb = raw.match(
        /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)$/i,
    );
    if (rgb) {
        const alpha = rgb[4] == null ? 1 : Number(rgb[4]);
        if (!Number.isFinite(alpha) || alpha <= 0) return undefined;
        const toByte = (part: string) => {
            const n = Number(part);
            if (!Number.isFinite(n)) return 0;
            return Math.max(0, Math.min(255, Math.round(n)));
        };
        const hex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
        return `#${hex(toByte(rgb[1]))}${hex(toByte(rgb[2]))}${hex(toByte(rgb[3]))}`;
    }
    // Named CSS colors — leave as-is; pdfmake accepts many common names.
    if (/^[a-z]+$/i.test(raw)) return raw.toLowerCase();
    return undefined;
};

const mergeMarksFromElement = (el: HTMLElement, marks: PdfTextMarks): PdfTextMarks => {
    const next: PdfTextMarks = { ...marks };

    if (['B', 'STRONG'].includes(el.tagName)) next.bold = true;
    if (['I', 'EM'].includes(el.tagName)) next.italics = true;
    if (el.tagName === 'U') next.decoration = 'underline';
    if (['S', 'STRIKE', 'DEL'].includes(el.tagName)) next.decoration = 'lineThrough';

    const fontWeight = String(el.style.fontWeight || '').trim().toLowerCase();
    if (
        fontWeight === 'bold' ||
        fontWeight === 'bolder' ||
        (Number.isFinite(Number(fontWeight)) && Number(fontWeight) >= 600)
    ) {
        next.bold = true;
    }
    const fontStyle = String(el.style.fontStyle || '').trim().toLowerCase();
    if (fontStyle === 'italic' || fontStyle === 'oblique') next.italics = true;

    const decoration = String(el.style.textDecoration || '').toLowerCase();
    if (decoration.includes('underline')) next.decoration = 'underline';
    if (decoration.includes('line-through')) next.decoration = 'lineThrough';

    const color = cssColorToPdf(el.style.color);
    if (color) next.color = color;
    const background = cssColorToPdf(el.style.backgroundColor || el.style.background);
    if (background) next.background = background;

    for (const className of Array.from(el.classList)) {
        if (className.startsWith('ql-color-')) {
            const token = className.slice('ql-color-'.length);
            next.color = cssColorToPdf(token) || next.color;
        }
        if (className.startsWith('ql-bg-')) {
            const token = className.slice('ql-bg-'.length);
            next.background = cssColorToPdf(token) || next.background;
        }
    }

    return next;
};

const makeTextRun = (text: string, marks: PdfTextMarks = {}) => {
    const normalized = normalizeInlineText(text);
    if (!normalized) return null;
    return {
        text: normalized,
        ...(marks.bold ? { bold: true } : {}),
        ...(marks.italics ? { italics: true } : {}),
        ...(marks.decoration ? { decoration: marks.decoration } : {}),
        ...(marks.color ? { color: marks.color } : {}),
        ...(marks.background ? { background: marks.background } : {}),
    };
};

/** Soft line break from `<br>` — stays inside one paragraph (no extra block margin). */
const SOFT_LINE_BREAK = '\n';

const isSoftLineBreakRun = (run: unknown): boolean =>
    run === SOFT_LINE_BREAK ||
    (Boolean(run) &&
        typeof run === 'object' &&
        String((run as { text?: unknown }).text ?? '') === SOFT_LINE_BREAK);

const extractInlineRuns = (node: ChildNode, marks: PdfTextMarks = {}): any[] => {
    if (node.nodeType === Node.TEXT_NODE) {
        const run = makeTextRun(node.textContent || '', marks);
        return run ? [run] : [];
    }
    if (!(node instanceof HTMLElement)) return [];
    // Soft break: same paragraph, just a new line (Quill Shift+Enter / <br>).
    if (node.tagName === 'BR') return [SOFT_LINE_BREAK];
    if (['IMG', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD', 'COLGROUP', 'COL'].includes(node.tagName)) {
        return [];
    }
    // Quill UI chrome inside cells — skip.
    if (node.classList.contains('ql-ui')) return [];

    const nextMarks = mergeMarksFromElement(node, marks);
    return Array.from(node.childNodes).flatMap((child) => extractInlineRuns(child, nextMarks));
};

const ensureSeparatorSpace = (out: any[]) => {
    if (!out.length || isSoftLineBreakRun(out[out.length - 1])) return;
    const last = out[out.length - 1];
    if (!last || typeof last.text !== 'string' || last.text.endsWith(' ')) return;
    out[out.length - 1] = { ...last, text: `${last.text} ` };
};

const trimRunTrailingSpace = (out: any[]) => {
    if (!out.length || isSoftLineBreakRun(out[out.length - 1])) return;
    const last = out[out.length - 1];
    if (!last || typeof last.text !== 'string') return;
    const text = last.text.replace(/ +$/, '');
    if (!text) {
        out.pop();
        return;
    }
    if (text !== last.text) out[out.length - 1] = { ...last, text };
};

/** Join inline runs the way HTML does: one space between tags, none at the block edges. */
const collapseInlineSpacing = (runs: any[]): any[] => {
    const out: any[] = [];
    for (const run of runs) {
        if (isSoftLineBreakRun(run)) {
            trimRunTrailingSpace(out);
            if (out.length && !isSoftLineBreakRun(out[out.length - 1])) out.push(SOFT_LINE_BREAK);
            continue;
        }
        const raw = String(run?.text ?? '').replace(/ +/g, ' ');
        if (!raw) continue;
        if (!raw.trim()) {
            ensureSeparatorSpace(out);
            continue;
        }
        const lead = raw.startsWith(' ');
        const trail = raw.endsWith(' ');
        const text = raw.trim();
        if (lead) ensureSeparatorSpace(out);
        out.push({ ...run, text: trail ? `${text} ` : text });
    }
    trimRunTrailingSpace(out);
    return out.filter((run) => isSoftLineBreakRun(run) || String(run?.text || '').length > 0);
};

/** Drop empty runs and normalize soft breaks for pdfmake text arrays. */
const normalizeInlineRuns = (runs: any[]): any[] => {
    const filtered = runs.filter((run) => {
        if (isSoftLineBreakRun(run)) return true;
        if (!run || typeof run !== 'object') return false;
        // Keep a whitespace-only run: it is the space between inline tags.
        return String(run.text ?? '').length > 0;
    });
    while (filtered.length && isSoftLineBreakRun(filtered[0])) filtered.shift();
    while (filtered.length && isSoftLineBreakRun(filtered[filtered.length - 1])) filtered.pop();
    const normalized: any[] = [];
    for (const run of filtered) {
        if (isSoftLineBreakRun(run) && normalized.length && isSoftLineBreakRun(normalized[normalized.length - 1])) {
            continue;
        }
        normalized.push(isSoftLineBreakRun(run) ? SOFT_LINE_BREAK : run);
    }
    if (!normalized.length || normalized.every(isSoftLineBreakRun)) return [];
    return collapseInlineSpacing(normalized);
};

const CELL_BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

const normalizeTextAlignment = (value: string): PdfTextAlignment | undefined => {
    const align = String(value || '')
        .trim()
        .toLowerCase();
    if (align === 'left' || align === 'start') return 'left';
    if (align === 'center') return 'center';
    if (align === 'right' || align === 'end') return 'right';
    if (align === 'justify') return 'justify';
    return undefined;
};

/** Quill stores align as `ql-align-*` on blocks; cell props may also set inline style / align attr. */
const readElementAlignment = (el: HTMLElement): PdfTextAlignment | undefined => {
    const fromStyle = normalizeTextAlignment(el.style.textAlign);
    if (fromStyle) return fromStyle;

    for (const className of Array.from(el.classList)) {
        if (!className.startsWith('ql-align-')) continue;
        const fromClass = normalizeTextAlignment(className.slice('ql-align-'.length));
        if (fromClass) return fromClass;
    }

    return normalizeTextAlignment(el.getAttribute('align') || '');
};

const readCellFillColor = (cell: HTMLElement): string | undefined =>
    cssColorToPdf(cell.style.backgroundColor || cell.style.background);

type PdfBorderSide = {
    width: number;
    color: string;
    style: string;
};

type PdfCellBorders = {
    left: PdfBorderSide;
    top: PdfBorderSide;
    right: PdfBorderSide;
    bottom: PdfBorderSide;
};

const NO_PDF_BORDER: PdfBorderSide = { width: 0, color: '#111111', style: 'none' };
/** Quill snow default inside `.ql-editor td/th`: `border: 1px solid #000` (1px = 0.75pt). */
const QUILL_DEFAULT_CELL_BORDER: PdfBorderSide = { width: 0.75, color: '#000000', style: 'solid' };
const INVISIBLE_BORDER_STYLES = new Set(['none', 'hidden', 'initial']);

const parseStyleMap = (styleAttr: string): Record<string, string> => {
    const out: Record<string, string> = {};
    String(styleAttr || '')
        .split(';')
        .forEach((part) => {
            const idx = part.indexOf(':');
            if (idx < 0) return;
            const key = part.slice(0, idx).trim().toLowerCase();
            const value = part.slice(idx + 1).trim();
            if (key) out[key] = value;
        });
    return out;
};

/** Convert CSS length to pdfmake points. Zero / invalid → 0. */
const cssLengthToPt = (value: string): number => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw || raw === '0' || raw === '0px' || raw === '0pt') return 0;
    if (raw === 'thin') return 0.5;
    if (raw === 'medium') return 1;
    if (raw === 'thick') return 2;
    const match = raw.match(/^(-?[\d.]+)\s*(px|pt|em|rem|mm|cm|in)?$/i);
    if (!match) return 0;
    const n = Number(match[1]);
    if (!Number.isFinite(n) || n <= 0) return 0;
    const unit = (match[2] || 'px').toLowerCase();
    if (unit === 'pt') return n;
    if (unit === 'mm') return (n * 72) / 25.4;
    if (unit === 'cm') return (n * 72) / 2.54;
    if (unit === 'in') return n * 72;
    if (unit === 'em' || unit === 'rem') return n * 9;
    return n * 0.75; // px → pt
};

const parseBorderShorthand = (value: string): Partial<PdfBorderSide> => {
    const raw = String(value || '').trim();
    if (!raw) return {};
    if (INVISIBLE_BORDER_STYLES.has(raw.toLowerCase())) {
        return { width: 0, style: 'none' };
    }
    const tokens = raw.split(/\s+/).filter(Boolean);
    let width: number | undefined;
    let style: string | undefined;
    let color: string | undefined;
    for (const token of tokens) {
        const lower = token.toLowerCase();
        if (
            [
                'none',
                'hidden',
                'solid',
                'dashed',
                'dotted',
                'double',
                'groove',
                'ridge',
                'inset',
                'outset',
            ].includes(lower)
        ) {
            style = lower;
            continue;
        }
        if (/^[\d.]/.test(token) || ['thin', 'medium', 'thick'].includes(lower)) {
            width = cssLengthToPt(token);
            continue;
        }
        const parsedColor = cssColorToPdf(token);
        if (parsedColor) color = parsedColor;
    }
    return {
        ...(width != null ? { width } : {}),
        ...(style ? { style } : {}),
        ...(color ? { color } : {}),
    };
};

const resolveBorderSide = (
    map: Record<string, string>,
    side: 'left' | 'top' | 'right' | 'bottom',
    fallback: PdfBorderSide = NO_PDF_BORDER,
): PdfBorderSide => {
    const sideShort = parseBorderShorthand(map[`border-${side}`] || '');
    const allShort = parseBorderShorthand(map.border || '');
    const styleRaw = String(
        map[`border-${side}-style`] ||
            sideShort.style ||
            map['border-style'] ||
            allShort.style ||
            '',
    )
        .trim()
        .toLowerCase();
    const colorRaw =
        map[`border-${side}-color`] ||
        sideShort.color ||
        map['border-color'] ||
        allShort.color ||
        fallback.color ||
        '';

    if (INVISIBLE_BORDER_STYLES.has(styleRaw)) {
        return { ...NO_PDF_BORDER };
    }

    const hasExplicitWidth =
        Boolean(map[`border-${side}-width`]) ||
        sideShort.width != null ||
        Boolean(map['border-width']) ||
        allShort.width != null ||
        Boolean(map[`border-${side}`]) ||
        Boolean(map.border);

    let width = 0;
    if (map[`border-${side}-width`]) {
        width = cssLengthToPt(map[`border-${side}-width`]);
    } else if (sideShort.width != null) {
        width = sideShort.width;
    } else if (map['border-width']) {
        width = cssLengthToPt(map['border-width']);
    } else if (allShort.width != null) {
        width = allShort.width;
    }

    // Explicit zero thickness → no border.
    if (hasExplicitWidth && (!width || width <= 0)) {
        return { ...NO_PDF_BORDER };
    }

    if (!width || width <= 0) {
        if (styleRaw && !INVISIBLE_BORDER_STYLES.has(styleRaw)) {
            // e.g. border-style: solid without width
            width = fallback.width > 0 ? fallback.width : 0.75;
        } else if (!styleRaw && fallback.width > 0 && !INVISIBLE_BORDER_STYLES.has(fallback.style)) {
            // Inherit table-level border when cell has no own border props.
            return { ...fallback };
        } else {
            return { ...NO_PDF_BORDER };
        }
    }

    return {
        width,
        color: cssColorToPdf(colorRaw) || fallback.color || '#111111',
        style: styleRaw || fallback.style || 'solid',
    };
};

const readElementBorders = (
    el: HTMLElement | null | undefined,
    fallback: PdfBorderSide = NO_PDF_BORDER,
): PdfCellBorders => {
    if (!el) {
        return { left: { ...fallback }, top: { ...fallback }, right: { ...fallback }, bottom: { ...fallback } };
    }
    const map = parseStyleMap(el.getAttribute('style') || '');
    const live = el.style;
    const assignIf = (key: string, value: string) => {
        const trimmed = String(value || '').trim();
        if (trimmed) map[key] = trimmed;
    };
    assignIf('border', live.border);
    assignIf('border-width', live.borderWidth);
    assignIf('border-style', live.borderStyle);
    assignIf('border-color', live.borderColor);
    assignIf('border-top', live.borderTop);
    assignIf('border-right', live.borderRight);
    assignIf('border-bottom', live.borderBottom);
    assignIf('border-left', live.borderLeft);
    assignIf('border-top-width', live.borderTopWidth);
    assignIf('border-right-width', live.borderRightWidth);
    assignIf('border-bottom-width', live.borderBottomWidth);
    assignIf('border-left-width', live.borderLeftWidth);
    assignIf('border-top-style', live.borderTopStyle);
    assignIf('border-right-style', live.borderRightStyle);
    assignIf('border-bottom-style', live.borderBottomStyle);
    assignIf('border-left-style', live.borderLeftStyle);
    assignIf('border-top-color', live.borderTopColor);
    assignIf('border-right-color', live.borderRightColor);
    assignIf('border-bottom-color', live.borderBottomColor);
    assignIf('border-left-color', live.borderLeftColor);

    return {
        left: resolveBorderSide(map, 'left', fallback),
        top: resolveBorderSide(map, 'top', fallback),
        right: resolveBorderSide(map, 'right', fallback),
        bottom: resolveBorderSide(map, 'bottom', fallback),
    };
};

const readTableFallbackBorder = (table: HTMLTableElement): PdfBorderSide => {
    const styleSources: HTMLElement[] = [table];
    const parent = table.parentElement;
    if (
        parent &&
        (parent.tagName === 'TEMPORARY' ||
            parent.classList.contains('ql-table-temporary') ||
            parent.classList.contains('ql-table-wrapper'))
    ) {
        styleSources.unshift(parent);
    }

    for (const source of styleSources) {
        const borders = readElementBorders(source, NO_PDF_BORDER);
        const fromStyle = [borders.left, borders.top, borders.right, borders.bottom].find(
            (side) => side.width > 0,
        );
        if (fromStyle) return fromStyle;
    }

    const borderAttr = table.getAttribute('border');
    if (borderAttr != null && String(borderAttr).trim() !== '') {
        const n = Number(borderAttr);
        if (!Number.isFinite(n) || n <= 0) return { ...NO_PDF_BORDER };
        return { width: Math.min(n, 4) * 0.75, color: '#111111', style: 'solid' };
    }
    return { ...NO_PDF_BORDER };
};

const pickStrongerBorder = (a: PdfBorderSide, b: PdfBorderSide): PdfBorderSide =>
    a.width >= b.width ? a : b;

const dashStyleForBorder = (side: PdfBorderSide): { dash: { length: number; space: number } } | null => {
    if (side.width <= 0) return null;
    if (side.style === 'dashed') return { dash: { length: 3, space: 2 } };
    if (side.style === 'dotted') return { dash: { length: 1, space: 1.5 } };
    return null;
};

const buildHtmlTableLayout = (borderGrid: PdfCellBorders[][]) => {
    const rowCount = borderGrid.length;
    const colCount = borderGrid[0]?.length || 0;

    const horizontalEdge = (lineIndex: number): PdfBorderSide => {
        let best = NO_PDF_BORDER;
        for (let c = 0; c < colCount; c++) {
            let side = NO_PDF_BORDER;
            if (lineIndex <= 0) side = borderGrid[0]?.[c]?.top || NO_PDF_BORDER;
            else if (lineIndex >= rowCount) side = borderGrid[rowCount - 1]?.[c]?.bottom || NO_PDF_BORDER;
            else {
                side = pickStrongerBorder(
                    borderGrid[lineIndex - 1]?.[c]?.bottom || NO_PDF_BORDER,
                    borderGrid[lineIndex]?.[c]?.top || NO_PDF_BORDER,
                );
            }
            best = pickStrongerBorder(best, side);
        }
        return best;
    };

    const verticalEdge = (lineIndex: number): PdfBorderSide => {
        let best = NO_PDF_BORDER;
        for (let r = 0; r < rowCount; r++) {
            let side = NO_PDF_BORDER;
            if (lineIndex <= 0) side = borderGrid[r]?.[0]?.left || NO_PDF_BORDER;
            else if (lineIndex >= colCount) side = borderGrid[r]?.[colCount - 1]?.right || NO_PDF_BORDER;
            else {
                side = pickStrongerBorder(
                    borderGrid[r]?.[lineIndex - 1]?.right || NO_PDF_BORDER,
                    borderGrid[r]?.[lineIndex]?.left || NO_PDF_BORDER,
                );
            }
            best = pickStrongerBorder(best, side);
        }
        return best;
    };

    return {
        defaultBorder: false,
        hLineWidth: (i: number) => horizontalEdge(i).width,
        vLineWidth: (i: number) => verticalEdge(i).width,
        hLineColor: (i: number) => horizontalEdge(i).color,
        vLineColor: (i: number) => verticalEdge(i).color,
        hLineStyle: (i: number) => dashStyleForBorder(horizontalEdge(i)),
        vLineStyle: (i: number) => dashStyleForBorder(verticalEdge(i)),
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3,
        paddingBottom: () => 3,
    };
};

/** A4 content width with the document page margins (40 + 40). */
const PDF_PAGE_CONTENT_WIDTH = 595.28 - 80;
const CELL_IMAGE_PADDING = 8;

const columnContentWidths = (widths: Array<string | number>): number[] => {
    const fixed = widths.map((width) => {
        if (typeof width === 'number' && width > 0) return width;
        if (typeof width === 'string' && width.endsWith('%')) {
            const percent = parseFloat(width);
            if (Number.isFinite(percent) && percent > 0) return (PDF_PAGE_CONTENT_WIDTH * percent) / 100;
        }
        return null;
    });
    const used = fixed.reduce((sum, width) => (sum||0) + (width || 0), 0);
    const starCount = fixed.filter((width) => width == null).length;
    const leftover = Math.max(0, PDF_PAGE_CONTENT_WIDTH - (used||0));
    const starWidth = starCount ? leftover / starCount : PDF_PAGE_CONTENT_WIDTH;
    return fixed.map((width) => Math.max(24, (width ?? starWidth) - CELL_IMAGE_PADDING));
};

const declaredLengthPt = (raw: string | null | undefined): number => {
    const value = String(raw || '').trim();
    if (!value || value.includes('%')) return 0;
    return cssLengthToPt(/[a-z]/i.test(value) ? value : `${value}px`);
};

const readDataUrlPixelSize = (dataUrl: string): Promise<{ width: number; height: number } | null> =>
    new Promise((resolve) => {
        if (typeof Image === 'undefined') {
            resolve(null);
            return;
        }
        const image = new Image();
        image.onload = () => {
            const width = image.naturalWidth || image.width || 0;
            const height = image.naturalHeight || image.height || 0;
            resolve(width > 0 && height > 0 ? { width, height } : null);
        };
        image.onerror = () => resolve(null);
        image.src = dataUrl;
    });

/**
 * pdfmake width in points from the picture's own pixels.
 * Only width is set, so height keeps the aspect ratio. Wider than maxWidth shrinks; smaller stays.
 */
const naturalImageWidthPt = async (
    img: HTMLImageElement,
    dataUrl: string,
    maxWidth: number,
): Promise<number> => {
    const cap = Math.max(1, maxWidth);
    let widthPt = declaredLengthPt(img.getAttribute('width'));
    const style = parseStyleMap(img.getAttribute('style') || '');
    if (!widthPt) widthPt = declaredLengthPt(style.width);
    const heightPt = declaredLengthPt(img.getAttribute('height')) || declaredLengthPt(style.height);
    if (!widthPt) {
        const natural = await readDataUrlPixelSize(dataUrl);
        if (natural) {
            const naturalWidthPt = cssLengthToPt(`${natural.width}px`);
            const naturalHeightPt = cssLengthToPt(`${natural.height}px`);
            if (heightPt > 0 && naturalHeightPt > 0) {
                widthPt = naturalWidthPt * (heightPt / naturalHeightPt);
            } else {
                widthPt = naturalWidthPt;
            }
        }
    }
    if (!widthPt || widthPt <= 0) return cap;
    return Math.min(widthPt, cap);
};

const imagesIn = (el: HTMLElement): HTMLImageElement[] =>
    Array.from(el.querySelectorAll('img')).filter((img) => {
        const owner = img.closest('table');
        return !owner || owner === el.closest('table');
    });

/** Quill puts `ql-align-*` on the paragraph, cell props put `text-align` on `td`. */
const readImageAlignment = (
    img: HTMLImageElement,
    fallback?: PdfTextAlignment,
): PdfTextAlignment | undefined => {
    let node: HTMLElement | null = img.parentElement;
    while (node && node.tagName !== 'BODY') {
        const align = readElementAlignment(node);
        if (align) return align;
        if (node.tagName === 'TD' || node.tagName === 'TH') break;
        node = node.parentElement;
    }
    return fallback;
};

/**
 * pdfmake ignores `alignment` on an image inside a table cell: the image box is only as
 * wide as the picture, so there is nothing to align. Spacer columns use the cell/page width.
 */
const layoutAlignedImage = (
    image: Record<string, unknown>,
    align: PdfTextAlignment | undefined,
    margin: [number, number, number, number],
): Record<string, unknown> => {
    if (align === 'center') {
        return {
            margin,
            columnGap: 0,
            columns: [{ width: '*', text: '' }, image, { width: '*', text: '' }],
        };
    }
    if (align === 'right') {
        return {
            margin,
            columnGap: 0,
            columns: [{ width: '*', text: '' }, image],
        };
    }
    return { ...image, margin };
};

const appendHtmlImages = async (
    target: Record<string, unknown>[],
    images: HTMLImageElement[],
    options: {
        resolveImageDataUrl?: (src: string) => Promise<string>;
        maxWidth: number;
        margin?: [number, number, number, number];
        alignment?: PdfTextAlignment;
        limit?: number;
    },
) => {
    const resolve = options.resolveImageDataUrl;
    if (!resolve) return;
    const margin = options.margin || [0, 4, 0, 4];
    const list = images.slice(0, options.limit ?? images.length);
    for (const image of list) {
        const src = String(image.getAttribute('src') || '').trim();
        if (!src) continue;
        const dataUrl = await resolve(src);
        if (!dataUrl) continue;
        const width = await naturalImageWidthPt(image, dataUrl, options.maxWidth);
        const align = readImageAlignment(image, options.alignment);
        target.push(
            layoutAlignedImage(
                { image: dataUrl, width },
                align === 'justify' ? 'left' : align,
                margin,
            ),
        );
    }
};

/** One pdfmake text block for a cell paragraph / list item (keeps own alignment + marks). */
const blockElementToPdfText = (
    block: HTMLElement,
    fallbackAlign?: PdfTextAlignment,
): Record<string, unknown> | null => {
    const runs = normalizeInlineRuns(
        Array.from(block.childNodes).flatMap((node) => extractInlineRuns(node)),
    );
    if (!runs.length) return null;
    const alignment = readElementAlignment(block) || fallbackAlign || 'left';
    return {
        text: runs,
        alignment,
        ...(block.tagName.startsWith('H') ? { bold: true } : {}),
    };
};

/** Build pdfmake cell content: stack of blocks so per-line align/format survive. */
const tableCellToPdfContent = async (
    cell: Element,
    isHeader: boolean,
    borders: PdfCellBorders,
    imageOptions: {
        resolveImageDataUrl?: (src: string) => Promise<string>;
        maxImageWidth: number;
    },
) => {
    const style = isHeader ? 'htmlTableHeader' : 'htmlTableCell';
    if (!(cell instanceof HTMLElement)) {
        return {
            text: '',
            style,
            border: [false, false, false, false],
        };
    }

    const cellAlign = readElementAlignment(cell);
    const fillColor = readCellFillColor(cell);
    const stack: Record<string, unknown>[] = [];

    const pushImages = async (images: HTMLImageElement[]) => {
        await appendHtmlImages(stack, images, {
            resolveImageDataUrl: imageOptions.resolveImageDataUrl,
            maxWidth: imageOptions.maxImageWidth,
            alignment: cellAlign,
        });
    };

    const pushDirectRuns = (nodes: ChildNode[]) => {
        const runs = normalizeInlineRuns(nodes.flatMap((node) => extractInlineRuns(node)));
        if (!runs.length) return;
        stack.push({
            text: runs,
            alignment: cellAlign || 'left',
        });
    };

    let looseNodes: ChildNode[] = [];
    const flushLoose = async () => {
        if (!looseNodes.length) return;
        const nodes = looseNodes;
        looseNodes = [];
        pushDirectRuns(nodes);
        for (const node of nodes) {
            if (node instanceof HTMLImageElement) {
                await pushImages([node]);
            } else if (node instanceof HTMLElement) {
                await pushImages(imagesIn(node));
            }
        }
    };

    for (const child of Array.from(cell.childNodes)) {
        if (child instanceof HTMLElement && CELL_BLOCK_TAGS.has(child.tagName)) {
            await flushLoose();
            const block = blockElementToPdfText(child, cellAlign);
            if (block) stack.push(block);
            await pushImages(imagesIn(child));
            continue;
        }
        if (child instanceof HTMLElement && (child.tagName === 'OL' || child.tagName === 'UL')) {
            await flushLoose();
            for (const li of Array.from(child.children)) {
                if (!(li instanceof HTMLElement) || li.tagName !== 'LI') continue;
                const block = blockElementToPdfText(li, cellAlign);
                if (block) stack.push(block);
                await pushImages(imagesIn(li));
            }
            continue;
        }
        looseNodes.push(child);
    }
    await flushLoose();

    const base: Record<string, unknown> = {
        style,
        border: [
            borders.left.width > 0,
            borders.top.width > 0,
            borders.right.width > 0,
            borders.bottom.width > 0,
        ],
        borderColor: [borders.left.color, borders.top.color, borders.right.color, borders.bottom.color],
        ...(fillColor ? { fillColor } : {}),
        ...(isHeader ? { bold: true } : {}),
    };

    if (!stack.length) {
        return { text: '', ...base, alignment: cellAlign || 'left' };
    }
    if (stack.length === 1) {
        return { ...stack[0], ...base };
    }
    return { stack, ...base };
};

/** pdfmake width: '%' string, points number, or '*' when the column has no own width. */
const cssWidthToPdf = (raw: string): string | number | null => {
    const value = String(raw || '').trim();
    if (!value) return null;
    if (value.endsWith('%')) {
        const n = parseFloat(value);
        if (!Number.isFinite(n) || n <= 0) return null;
        return `${n}%`;
    }
    // `<col width="120">` from quill-table-better is pixels without a unit.
    const pt = cssLengthToPt(/[a-z%]/i.test(value) ? value : `${value}px`);
    return pt > 0 ? pt : null;
};

const readElementCssWidth = (el: Element): string | number | null => {
    const style = parseStyleMap(el.getAttribute('style') || '');
    return cssWidthToPdf(style.width || el.getAttribute('width') || '');
};

const readHtmlTableWidths = (
    table: HTMLTableElement,
    colCount: number,
    rows: Element[][],
): Array<string | number> => {
    const colgroups = Array.from(table.children).filter((el) => el.tagName === 'COLGROUP');
    const cols = colgroups.length
        ? colgroups.flatMap((group) => Array.from(group.children).filter((el) => el.tagName === 'COL'))
        : Array.from(table.children).filter((el) => el.tagName === 'COL');

    const fromCols =
        cols.length === colCount ? cols.map((col) => readElementCssWidth(col) ?? '*') : null;
    if (fromCols?.some((width) => width !== '*')) return fromCols;

    const firstRow = rows[0] || [];
    if (firstRow.length === colCount) {
        const fromCells = firstRow.map((cell) =>
            cell instanceof HTMLElement ? (readElementCssWidth(cell) ?? '*') : '*',
        );
        if (fromCells.some((width) => width !== '*')) return fromCells;
    }

    return Array.from({ length: colCount }, () => '*');
};

const htmlTableToPdfBlock = async (
    table: HTMLTableElement,
    resolveImageDataUrl?: (src: string) => Promise<string>,
) => {
    const rowElements = Array.from(table.rows);
    if (!rowElements.length) return null;

    const rows = rowElements.map((row) => Array.from(row.cells));
    const colCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
    if (!colCount) return null;
    const headerRowsCount =
        rows.length && rows[0].some((cell) => cell.tagName === 'TH') ? 1 : 0;

    const tableFallback = readTableFallbackBorder(table);
    const borderAttr = table.getAttribute('border');
    const tableDeclaresBorder = borderAttr != null && String(borderAttr).trim() !== '';
    // Fresh Quill cells have no inline border; the 1px frame exists only in editor CSS.
    const cellFallback =
        tableFallback.width > 0
            ? tableFallback
            : tableDeclaresBorder
              ? NO_PDF_BORDER
              : QUILL_DEFAULT_CELL_BORDER;
    const borderGrid: PdfCellBorders[][] = rows.map((cells) => {
        const padded = [...cells] as (Element | null)[];
        while (padded.length < colCount) padded.push(null);
        return padded.map((cell) =>
            cell instanceof HTMLElement
                ? readElementBorders(cell, cellFallback)
                : {
                      left: { ...cellFallback },
                      top: { ...cellFallback },
                      right: { ...cellFallback },
                      bottom: { ...cellFallback },
                  },
        );
    });

    const widths = readHtmlTableWidths(table, colCount, rows);
    const imageWidths = columnContentWidths(widths);
    const body: Record<string, unknown>[][] = [];
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
        const cells = rows[rowIndex];
        const padded = [...cells] as (Element | null)[];
        while (padded.length < colCount) padded.push(null);
        const row: Record<string, unknown>[] = [];
        for (let colIndex = 0; colIndex < padded.length; colIndex += 1) {
            const cell = padded[colIndex];
            const borders = borderGrid[rowIndex][colIndex];
            if (!cell) {
                row.push({
                    text: '',
                    style: 'htmlTableCell',
                    border: [
                        borders.left.width > 0,
                        borders.top.width > 0,
                        borders.right.width > 0,
                        borders.bottom.width > 0,
                    ],
                    borderColor: [borders.left.color, borders.top.color, borders.right.color, borders.bottom.color],
                });
                continue;
            }
            row.push(
                await tableCellToPdfContent(cell, cell.tagName === 'TH', borders, {
                    resolveImageDataUrl,
                    maxImageWidth: imageWidths[colIndex] ?? PDF_PAGE_CONTENT_WIDTH,
                }),
            );
        }
        body.push(row);
    }

    return {
        table: {
            headerRows: headerRowsCount,
            widths,
            body,
        },
        layout: buildHtmlTableLayout(borderGrid),
        margin: [0, 4, 0, 8],
    };
};

const htmlToPdfBlocks = async (
    html: string,
    options: {
        paragraphStyle?: string;
        headingStyle?: string;
        resolveImageDataUrl?: (src: string) => Promise<string>;
        maxBlocks?: number;
    } = {},
): Promise<any[]> => {
    if (!hasHtmlContent(html)) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const blocks: any[] = [];
    const maxBlocks = options.maxBlocks || 160;

    const pushTextBlock = (runs: any[], style: string, alignment?: PdfTextAlignment) => {
        const normalized = normalizeInlineRuns(runs);
        if (!normalized.length) return;
        blocks.push({
            text: normalized,
            style,
            ...(alignment && alignment !== 'left' ? { alignment } : {}),
        });
    };

    for (const node of Array.from(doc.body.childNodes)) {
        if (blocks.length >= maxBlocks) break;
        if (node.nodeType === Node.TEXT_NODE) {
            const run = makeTextRun(node.textContent || '', {});
            pushTextBlock(run ? [run] : [], options.paragraphStyle || 'paragraph');
            continue;
        }
        if (!(node instanceof HTMLElement)) continue;

        if (node.tagName === 'TABLE') {
            const tableBlock = await htmlTableToPdfBlock(node as HTMLTableElement, options.resolveImageDataUrl);
            if (tableBlock) blocks.push(tableBlock);
            continue;
        }

        const directTables = Array.from(node.querySelectorAll<HTMLTableElement>(':scope > table'));
        if (directTables.length) {
            for (const table of directTables) {
                if (blocks.length >= maxBlocks) break;
                const tableBlock = await htmlTableToPdfBlock(table, options.resolveImageDataUrl);
                if (tableBlock) blocks.push(tableBlock);
            }
            // Quill table wrappers are often a div that only contains the table — don't re-parse as text.
            const hasNonTableContent = Array.from(node.childNodes).some((child) => {
                if (child.nodeType === Node.TEXT_NODE) return Boolean(String(child.textContent || '').trim());
                if (!(child instanceof HTMLElement)) return false;
                return child.tagName !== 'TABLE' && child.tagName !== 'TEMPORARY';
            });
            if (!hasNonTableContent) continue;
        }

        if (node.tagName === 'IMG') {
            if (blocks.length < maxBlocks) {
                await appendHtmlImages(blocks, [node as HTMLImageElement], {
                    resolveImageDataUrl: options.resolveImageDataUrl,
                    maxWidth: PDF_PAGE_CONTENT_WIDTH,
                    margin: [0, 6, 0, 10],
                    alignment: readElementAlignment(node),
                });
            }
            continue;
        }

        if (node.tagName === 'UL' || node.tagName === 'OL') {
            const listItems = Array.from(node.querySelectorAll(':scope > li'))
                .map((li) => {
                    if (!(li instanceof HTMLElement)) return null;
                    const runs = normalizeInlineRuns(
                        Array.from(li.childNodes).flatMap((child) => extractInlineRuns(child)),
                    );
                    if (!runs.length) return null;
                    const alignment = readElementAlignment(li);
                    if (runs.length === 1 && typeof runs[0] === 'object' && !alignment) {
                        return runs[0];
                    }
                    return {
                        text: runs,
                        ...(alignment && alignment !== 'left' ? { alignment } : {}),
                    };
                })
                .filter(Boolean)
                .slice(0, 40);
            if (listItems.length) {
                const listAlign = readElementAlignment(node);
                blocks.push({
                    [node.tagName === 'OL' ? 'ol' : 'ul']: listItems,
                    style: options.paragraphStyle || 'paragraph',
                    margin: [0, 0, 0, 8],
                    ...(listAlign && listAlign !== 'left' ? { alignment: listAlign } : {}),
                });
            }
            continue;
        }

        const inlineRuns = extractInlineRuns(node);
        const headingStyles: Record<string, string> = {
            H1: 'htmlH1',
            H2: 'htmlH2',
            H3: 'htmlH3',
            H4: 'htmlH4',
            H5: 'htmlH5',
            H6: 'htmlH6',
        };
        const headingStyleName = headingStyles[node.tagName];
        // Prefer per-level styles; optional headingStyle forces one style for all headings.
        const style = headingStyleName
            ? options.headingStyle || headingStyleName
            : options.paragraphStyle || 'paragraph';
        if (headingStyleName) {
            inlineRuns.forEach((run) => {
                if (run && typeof run === 'object' && 'text' in run) run.bold = true;
            });
        }
        pushTextBlock(inlineRuns, style, readElementAlignment(node));

        const inlineImages = imagesIn(node).slice(0, 8);
        if (blocks.length < maxBlocks && inlineImages.length) {
            const room = maxBlocks - blocks.length;
            await appendHtmlImages(blocks, inlineImages, {
                resolveImageDataUrl: options.resolveImageDataUrl,
                maxWidth: PDF_PAGE_CONTENT_WIDTH,
                margin: [0, 6, 0, 10],
                alignment: readElementAlignment(node),
                limit: room,
            });
        }
    }

    return blocks.slice(0, maxBlocks);
};

export const buildProposalPdfDefinition = async ({
    t,
    tObjects,
    templateValueKeys,
    formData,
    rate,
    generatedAt,
    objectData,
    logoDataUrl = '',
    backgroundDataUrl = '',
    resolveImageDataUrl,
    includeCoverPage = false,
    appendSpecificationWithoutMarker = true,
}: BuildProposalPdfDefinitionParams) => {
    const { buildTemplateValueKeysMap, replaceTemplateValueKeys } = templateValueKeys;
    const templateHasWarrantyTable = /\{warrantyProducts\}/i.test(
        normalizeTemplateMarkers(
            `${formData.proposalTitle || ''}${formData.proposalIntro || ''}${formData.proposalOutro || ''}`,
        ),
    );
    const warrantyRows = templateHasWarrantyTable ? await loadObjectWarranties(objectData) : [];
    const useUsd = getProposalUseUsd(formData.pricesInUsd, rate);
    const withVat = Boolean(formData.proposalVat);
    const currencyLabel = useUsd
        ? t('edit.preview.currencyUsd', getCurrencyI18nParams())
        : t('edit.preview.currencyUah', getCurrencyI18nParams());
    const lines = await fillMissingLineDimensions(formData.products || [], formData.additionalItems || []); 
    const productRows = lines.products.map((row) => mapProductRow(row, rate, useUsd, withVat, t));
    const additionalRows = lines.additionalItems.map((row) => mapAdditionalRow(row, rate, useUsd, withVat, t));
    const serviceRows = additionalRows.filter((_, index) => !lines.additionalItems[index].asOtherExpense);
    const otherRows = additionalRows.filter((_, index) => lines.additionalItems[index].asOtherExpense);
    const specificationRows = [...productRows, ...additionalRows];
    const total = getProposalTotalAmount(formData, rate);
    const totalVat = getProposalTotalVatAmount(formData, rate);
    const sumRows = (rows: ProposalPdfRow[]) =>
        rows.reduce(
            (acc, row) => ({
                total: acc.total + Number(row.total || 0),
                vat: acc.vat + Number(row.vat || 0),
            }),
            { total: 0, vat: 0 },
        );
    const productsSummary = sumRows(productRows);
    const additionalsSummary = sumRows(additionalRows);
    const servicesSummary = sumRows(serviceRows);
    const otherItemsSummary = sumRows(otherRows);
    const specificationSummary = sumRows(specificationRows);
    const generatedDate = DT.fromMillis(generatedAt).toFormat('dd.MM.yyyy HH:mm');
    const valueKeysMap = {
        ...buildObjectTemplateValueKeysMap({
            objectData,
            tObjects: tObjects as never,
        }),
        ...buildDocumentTemplateValueKeysMap({
            proposalName: formData.proposalName,
            sourceDocument: formData.sourceDocument,
            proposalNumber: formData.proposalNumber,
            proposalDt: formData.proposalDt,
        }),
        ...buildTemplateValueKeysMap({
            productsAmount: productsSummary.total,
            additionalsAmount: additionalsSummary.total,
            specificationAmount: specificationSummary.total,
            servicesAmount: servicesSummary.total,
            otherItemsAmount: otherItemsSummary.total,
            productsAmountVat: productsSummary.vat,
            additionalsAmountVat: additionalsSummary.vat,
            specificationAmountVat: specificationSummary.vat,
            servicesAmountVat: servicesSummary.vat,
            otherItemsAmountVat: otherItemsSummary.vat,
            currencyCode: useUsd ? getBaseCurrencyCode() : getCurrencyCode(),
        }),
    };
    const titleHtml = normalizeTemplateMarkers(
        replaceTemplateValueKeys(String(formData.proposalTitle || ''), valueKeysMap),
    );
    const introHtml = normalizeTemplateMarkers(
        replaceTemplateValueKeys(String(formData.proposalIntro || ''), valueKeysMap),
    );
    const outroHtml = normalizeTemplateMarkers(
        replaceTemplateValueKeys(String(formData.proposalOutro || ''), valueKeysMap),
    );
    const titleHasMarker = hasTemplateTableMarker(titleHtml);

    const headerRow = [
        { text: '№', style: 'tableHeader', alignment: 'center' as const },
        { text: t('edit.preview.tableName'), style: 'tableHeader' },
        { text: t('edit.preview.tableDimension'), style: 'tableHeader', alignment: 'center' },
        { text: t('edit.preview.tableCount'), style: 'tableHeader', alignment: 'right' },
        { text: `${t('edit.preview.tablePrice')} ${currencyLabel}`, style: 'tableHeader', alignment: 'right' },
        { text: `${t('edit.preview.tableTotal')} ${currencyLabel}`, style: 'tableHeader', alignment: 'right' },
        ...(withVat
            ? [
                  {
                      text: `${t('edit.preview.tableVat')} ${currencyLabel}`,
                      style: 'tableHeader',
                      alignment: 'right' as const,
                  },
              ]
            : []),
    ];

    const labelColSpan = 5;
    const emptyCells = [{}, {}, {}, {}];
    const tableWidths = withVat ? [22, '*', 36, 46, 68, 72, 72] : [22, '*', 42, 58, 84, 84];
    const createTableBlock = (tableRows: ProposalPdfRow[], totalValue: number, totalVatValue: number) =>
        tableRows.length
            ? {
                  table: {
                      headerRows: 1,
                      widths: tableWidths,
                      body: [
                          headerRow,
                          ...tableRows.map((row, index) => [
                              { text: String(index + 1), style: 'tableCell', alignment: 'center' as const },
                              { text: normalizeLine(row.name).slice(0, 220), style: 'tableCell' },
                              { text: row.unit, style: 'tableCell', alignment: 'center' },
                              { text: formatPrice(row.count), style: 'tableCell', alignment: 'right' },
                              { text: formatPrice(row.price), style: 'tableCell', alignment: 'right' },
                              { text: formatPrice(row.total), style: 'tableCell', alignment: 'right' },
                              ...(withVat
                                  ? [{ text: formatPrice(row.vat), style: 'tableCell', alignment: 'right' as const }]
                                  : []),
                          ]),
                          [
                              {
                                  text: `${withVat ? t('edit.preview.totalWithVatLabel') : t('edit.preview.totalLabel')} ${currencyLabel}`,
                                  colSpan: labelColSpan,
                                  style: 'tableTotalLabel',
                                  alignment: 'right',
                              },
                              ...emptyCells,
                              { text: formatPrice(totalValue), style: 'tableTotalValue', alignment: 'right' },
                              ...(withVat
                                  ? [{ text: formatPrice(totalVatValue), style: 'tableTotalValue', alignment: 'right' as const }]
                                  : []),
                          ],
                      ],
                  },
                  margin: [0, 0, 0, 8],
                  layout: {
                      // Must stay true: pdfmake skips hLines when defaultBorder=false
                      // and cells have no explicit border[].
                      defaultBorder: true,
                      hLineWidth: (i: number, node: { table: { body: unknown[] } }) => {
                          const last = node.table.body.length;
                          // Outer frame + under header + above total
                          if (i === 0 || i === last || i === 1 || i === last - 1) return 1.1;
                          // Light separators between item rows
                          return 0.7;
                      },
                      vLineWidth: () => 0,
                      hLineColor: (i: number, node: { table: { body: unknown[] } }) => {
                          const last = node.table.body.length;
                          if (i === 0 || i === 1 || i === last - 1 || i === last) {
                              return '#111111';
                          }
                          return '#9CA3AF';
                      },
                      paddingLeft: () => 4,
                      paddingRight: () => 4,
                      paddingTop: () => 4,
                      paddingBottom: () => 4,
                  },
              }
            : null;

    const createWarrantyTableBlock = () =>
        warrantyRows.length
            ? {
                  table: {
                      headerRows: 1,
                      widths: [22, '*', 160, 72],
                      body: [
                          [
                              { text: '№', style: 'tableHeader', alignment: 'center' as const },
                              { text: t('edit.preview.tableWarrantyProduct'), style: 'tableHeader' },
                              { text: t('edit.preview.tableWarrantySerial'), style: 'tableHeader' },
                              {
                                  text: t('edit.preview.tableWarrantyUntil'),
                                  style: 'tableHeader',
                                  alignment: 'center' as const,
                              },
                          ],
                          ...warrantyRows.map((row, index) => [
                              { text: String(index + 1), style: 'tableCell', alignment: 'center' as const },
                              { text: normalizeLine(row.prodName), style: 'tableCell' },
                              { text: normalizeLine(row.serialNumber), style: 'tableCell' },
                              {
                                  text: formatWarrantyDate(row.warrantyUntil),
                                  style: 'tableCell',
                                  alignment: 'center' as const,
                              },
                          ]),
                      ],
                  },
                  margin: [0, 0, 0, 8],
                  layout: {
                      defaultBorder: true,
                      hLineWidth: (i: number, node: { table: { body: unknown[] } }) => {
                          const last = node.table.body.length;
                          if (i === 0 || i === 1 || i === last) return 1.1;
                          return 0.7;
                      },
                      vLineWidth: () => 0,
                      hLineColor: (i: number, node: { table: { body: unknown[] } }) => {
                          const last = node.table.body.length;
                          if (i === 0 || i === 1 || i === last) return '#111111';
                          return '#9CA3AF';
                      },
                      paddingLeft: () => 4,
                      paddingRight: () => 4,
                      paddingTop: () => 4,
                      paddingBottom: () => 4,
                  },
              }
            : null;

    const createTableBlockByMarker = (marker: string) => {
        if (marker === 'warrantyproducts') {
            return createWarrantyTableBlock();
        }
        if (marker === 'products') {
            return createTableBlock(productRows, productsSummary.total, productsSummary.vat);
        }
        if (marker === 'additionals') {
            return createTableBlock(additionalRows, additionalsSummary.total, additionalsSummary.vat);
        }
        if (marker === 'services') {
            return createTableBlock(serviceRows, servicesSummary.total, servicesSummary.vat);
        }
        if (marker === 'otheritems') {
            return createTableBlock(otherRows, otherItemsSummary.total, otherItemsSummary.vat);
        }
        return createTableBlock(specificationRows, total, totalVat);
    };

    const buildSectionBlocks = async (
        html: string,
        fallbackAppendTable: boolean,
        paragraphStyle: string = 'paragraph',
    ): Promise<any[]> => {
        const blocks: any[] = [];
        const sourceHtml = normalizeTemplateMarkers(html);
        const markerMatches = sourceHtml.match(TEMPLATE_MARKER_REGEX);
        const hasMarker = Boolean(markerMatches?.length);

        if (hasMarker) {
            let segmentStart = 0;
            const regex = new RegExp(TEMPLATE_MARKER_REGEX);
            let match: RegExpExecArray | null = null;

            while ((match = regex.exec(sourceHtml))) {
                const segment = sourceHtml.slice(segmentStart, match.index);
                const partBlocks = await htmlToPdfBlocks(segment, {
                    paragraphStyle,
                    resolveImageDataUrl,
                    maxBlocks: 120,
                });
                blocks.push(...partBlocks);
                const marker = String(match[1] || '').toLowerCase();
                const table = createTableBlockByMarker(marker);
                if (table) blocks.push(table);
                segmentStart = match.index + match[0].length;
            }

            const tailSegment = sourceHtml.slice(segmentStart);
            const tailBlocks = await htmlToPdfBlocks(tailSegment, {
                paragraphStyle,
                resolveImageDataUrl,
                maxBlocks: 120,
            });
            blocks.push(...tailBlocks);
            return blocks;
        }

        const textBlocks = await htmlToPdfBlocks(String(sourceHtml || ''), {
            paragraphStyle,
            resolveImageDataUrl,
            maxBlocks: 120,
        });
        blocks.push(...textBlocks);
        if (fallbackAppendTable) {
            const table = createTableBlockByMarker('specification');
            if (table) blocks.push(table);
        }
        return blocks;
    };

    const introHasMarker = hasTemplateTableMarker(introHtml);
    const outroHasMarker = hasTemplateTableMarker(outroHtml);
    const introHasText = hasHtmlContent(introHtml);
    const outroHasText = hasHtmlContent(outroHtml);
    const titleHasText = hasHtmlContent(titleHtml);
    const titleBlocks = titleHasText
        ? await buildSectionBlocks(
              titleHtml,
              false,
              titleHasMarker ? 'paragraph' : 'docLeadText',
          )
        : [];
    const introBlocks = introHasText
        ? await buildSectionBlocks(
              introHtml,
              appendSpecificationWithoutMarker
                  && !introHasMarker
                  && !outroHasMarker
                  && !(includeCoverPage && titleHasMarker),
          )
        : [];
    const outroBlocks = outroHasText ? await buildSectionBlocks(outroHtml, false) : [];

    const contentBlocks: any[] = [];
    if (introBlocks.length) {
        contentBlocks.push(...introBlocks);
    }
    if (outroBlocks.length) {
        if (introBlocks.length) {
            contentBlocks.push({ text: '', pageBreak: 'before' });
        }
        contentBlocks.push(...outroBlocks);
    }

    const coverHeaderStack = [
        ...(logoDataUrl
            ? [
                  {
                      image: logoDataUrl,
                      width: 120,
                      margin: [0, 0, 0, 24] as [number, number, number, number],
                  },
              ]
            : []),
        { text: t('edit.preview.mainTitle'), style: 'docTitle' },
    ];

    const coverContent = titleHasMarker
        ? [
              {
                  stack: [
                      {
                          alignment: 'center',
                          margin: [0, 48, 0, 16] as [number, number, number, number],
                          stack: coverHeaderStack,
                      },
                      ...titleBlocks,
                  ],
              },
              { text: '', pageBreak: 'after' },
          ]
        : [
              {
                  margin: [0, 180, 0, 0] as [number, number, number, number],
                  alignment: 'center',
                  stack: [...coverHeaderStack, ...titleBlocks],
              },
              { text: '', pageBreak: 'after' },
          ];

    return {
        pageSize: 'A4',
        pageMargins: [40, 40, 40, 40],
        background: backgroundDataUrl
            ? (_currentPage: number, pageSize: { width: number; height: number }) => ({
                  stack: [
                      {
                          image: backgroundDataUrl,
                          cover: {
                              width: pageSize.width,
                              height: pageSize.height,
                              align: 'center',
                              valign: 'center',
                          },
                      },
                      {
                          canvas: [
                              {
                                  type: 'rect',
                                  x: 0,
                                  y: 0,
                                  w: pageSize.width,
                                  h: pageSize.height,
                                  color: '#FFFFFF',
                              },
                          ],
                          opacity: 0.7,
                      },
                  ],
              })
            : undefined,
        content: [
            ...(includeCoverPage ? coverContent : []),
            ...contentBlocks,
            {
                text: t('edit.generate.generatedAtLabel', { date: generatedDate }),
                style: 'docMeta',
                margin: [0, 20, 0, 0],
            },
        ],
        styles: {
            docTitle: {
                fontSize: 20,
                bold: true,
                color: '#111827',
                margin: [0, 0, 0, 8],
            },
            docSubtitle: {
                fontSize: 12,
                color: '#4B5563',
                margin: [0, 0, 0, 12],
            },
            docLeadText: {
                fontSize: 10.5,
                lineHeight: 1.35,
                color: '#1F2937',
                margin: [0, 0, 0, 3],
            },
            docMeta: {
                margin: [0, 16, 0, 0],
                color: '#9CA3AF',
                fontSize: 8.5,
            },
            sectionTitle: {
                fontSize: 12,
                bold: true,
                color: '#111827',
                margin: [0, 2, 0, 8],
            },
            htmlH1: {
                fontSize: 16,
                bold: true,
                color: '#111827',
                lineHeight: 1.25,
                margin: [0, 0, 0, 8],
            },
            htmlH2: {
                fontSize: 13.5,
                bold: true,
                color: '#111827',
                lineHeight: 1.3,
                margin: [0, 2, 0, 6],
            },
            htmlH3: {
                fontSize: 12,
                bold: true,
                color: '#111827',
                lineHeight: 1.35,
                margin: [0, 2, 0, 6],
            },
            htmlH4: {
                fontSize: 10.5,
                bold: true,
                color: '#111827',
                lineHeight: 1.35,
                margin: [0, 2, 0, 5],
            },
            htmlH5: {
                fontSize: 9.5,
                bold: true,
                color: '#111827',
                lineHeight: 1.35,
                margin: [0, 1, 0, 4],
            },
            htmlH6: {
                fontSize: 9,
                bold: true,
                color: '#374151',
                lineHeight: 1.35,
                margin: [0, 1, 0, 4],
            },
            paragraph: {
                fontSize: 9.5,
                lineHeight: 1.45,
                color: '#1F2937',
                margin: [0, 0, 0, 6],
            },
            docMutedText: {
                fontSize: 9.5,
                color: '#9CA3AF',
                italics: true,
            },
            tableHeader: {
                bold: true,
                fontSize: 9.5,
                color: '#111827',
                margin: [0, 4, 0, 4],
                fillColor: '#F3F4F6',
            },
            tableCell: {
                fontSize: 9.5,
                color: '#1F2937',
                margin: [0, 3, 0, 3],
            },
            tableTotalLabel: {
                bold: true,
                fontSize: 9.5,
                margin: [0, 4, 0, 4],
            },
            tableTotalValue: {
                bold: true,
                fontSize: 9.5,
                color: '#111827',
                margin: [0, 4, 0, 4],
            },
            htmlTableHeader: {
                bold: true,
                fontSize: 9.5,
                color: '#111827',
                margin: [0, 2, 0, 2],
            },
            htmlTableCell: {
                fontSize: 9.5,
                color: '#1F2937',
                margin: [0, 2, 0, 2],
            },
        },
        defaultStyle: {
            fontSize: 9.5,
            color: '#1F2937',
            lineHeight: 1.4,
        },
    };
};
